/**
 * Daycare Data Processing Utilities
 * Pure functions for daycare-related data transformations
 */
import { normalizeText } from './dataUtils';

/**
 * Package type configurations
 */
export const PACKAGE_TYPES = {
    '1-day': { label: '1 Día', days: 1 },
    '15-day': { label: '15 Días', days: 15 },
    '30-day': { label: '30 Días', days: 30 }
};

/**
 * Filter packages by search term (dog name, package type)
 * @param {Array} packages - Array of packages to filter
 * @param {string} searchTerm - Search term
 * @returns {Array} Filtered packages array
 */
export function filterPackagesBySearch(packages, searchTerm) {
    if (!searchTerm) return packages;

    const lowerSearch = normalizeText(searchTerm);
    return packages.filter(pkg =>
        normalizeText(pkg.dog?.name).includes(lowerSearch) ||
        normalizeText(pkg.packageType).includes(lowerSearch)
    );
}

/**
 * Sort packages by different criteria
 * @param {Array} packages - Array of packages to sort
 * @param {string} sortBy - Sort criteria ('dogName', 'packageType', 'daysLeft', 'expiresAt')
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {Array} Sorted packages array
 */
export function sortPackages(packages, sortBy = 'dogName', sortDirection = 'asc') {
    const sorted = [...packages].sort((a, b) => {
        let aValue, bValue;
        
        switch (sortBy) {
            case 'dogName':
                aValue = a.dog?.name?.toLowerCase() || '';
                bValue = b.dog?.name?.toLowerCase() || '';
                break;
            case 'packageType':
                aValue = a.packageType || '';
                bValue = b.packageType || '';
                break;
            case 'daysLeft':
                aValue = a.daysLeft || 0;
                bValue = b.daysLeft || 0;
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            case 'expiresAt':
                aValue = new Date(a.expiresAt);
                bValue = new Date(b.expiresAt);
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            default:
                aValue = '';
                bValue = '';
        }
        
        if (sortDirection === 'asc') {
            return aValue.localeCompare(bValue);
        } else {
            return bValue.localeCompare(aValue);
        }
    });
    
    return sorted;
}

/**
 * Filter packages to only include valid ones (with dog data)
 * @param {Array} packages - Array of packages to filter
 * @returns {Array} Valid packages array
 */
export function filterValidPackages(packages) {
    return packages.filter(pkg => pkg.dog && pkg.dog.name);
}

/**
 * Get dogs available for check-in (not already checked in today)
 * @param {Array} dogs - All dogs
 * @param {Array} visits - Today's visits
 * @returns {Array} Dogs available for check-in
 */
export function getDogsAvailableForCheckin(dogs, visits) {
    return dogs.filter(dog => {
        // Filter out dogs already checked in today and not checked out
        return !visits.some(visit => 
            visit.dog._id === dog._id && 
            visit.checkInTime &&
            !visit.checkOutTime
        );
    });
}

/**
 * Get visits available for check-out (checked in but not checked out)
 * @param {Array} visits - Today's visits
 * @returns {Array} Visits available for check-out
 */
export function getVisitsAvailableForCheckout(visits) {
    return visits.filter(visit => visit.checkInTime && !visit.checkOutTime);
}

/**
 * Transform package form data for API submission
 * @param {Object} formData - Form data { dogId, packageType }
 * @returns {Object} Transformed data for API
 */
export function transformPackageFormData({ dogId, packageType }) {
    return {
        dog: dogId,
        packageType
    };
}

/**
 * Transform visit form data for API submission (check-in)
 * @param {Object} formData - Form data { dogId, notes }
 * @returns {Object} Transformed data for API
 */
export function transformCheckinFormData({ dogId, notes, packageId, visitType }) {
    const now = new Date();
    const today = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');
    return {
        dog: dogId,
        date: today,
        checkInTime: new Date().toISOString(),
        notes: notes?.trim() || '',
        ...(packageId && { packageId }),
        ...(visitType && { visitType }),
    };
}

/**
 * Checkout mood options — 3-face selector: green :) yellow :| red :(
 */
export const CHECKOUT_MOODS = [
    { value: 'happy',   label: 'Bien',      emoji: '😊', color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7', desc: 'Jugó bien, tranquilo' },
    { value: 'neutral', label: 'Regular',   emoji: '😐', color: '#f59e0b', bg: '#fffbeb', border: '#fcd34d', desc: 'Algo inquieto' },
    { value: 'sad',     label: 'Atención',  emoji: '😟', color: '#ef4444', bg: '#fef2f2', border: '#fca5a5', desc: 'Requiere atención' },
];

/**
 * Transform checkout form data for API submission
 * @param {string|Object} notesOrObj - Checkout notes string or { notes, checkoutMood, checkoutBehaviorNotes }
 * @returns {Object} Transformed data for API
 */
export function transformCheckoutFormData(notesOrObj) {
    let notes = '';
    let checkoutMood = null;
    let checkoutBehaviorNotes = '';
    if (typeof notesOrObj === 'string' || notesOrObj == null) {
        notes = notesOrObj;
    } else {
        notes = notesOrObj.notes;
        checkoutMood = notesOrObj.checkoutMood || null;
        checkoutBehaviorNotes = notesOrObj.checkoutBehaviorNotes || '';
    }
    return {
        checkOutTime: new Date().toISOString(),
        notes: notes?.trim() || '',
        ...(checkoutMood && { checkoutMood }),
        ...(checkoutBehaviorNotes?.trim() && { checkoutBehaviorNotes: checkoutBehaviorNotes.trim().slice(0, 1000) }),
    };
}

/**
 * Get package status based on days left
 * @param {number} daysLeft - Days left in package
 * @returns {Object} Status object with type and label
 */
export function getPackageStatus(daysLeft) {
    if (daysLeft <= 0) {
        return { type: 'expired', label: 'Vencido' };
    } else if (daysLeft <= 3) {
        return { type: 'expiring', label: 'Por vencer' };
    } else {
        return { type: 'active', label: 'Activo' };
    }
}

/**
 * Get active warnings for a package (low days, expiring soon)
 * @param {Object} pkg - Package object
 * @returns {Array} Array of warning objects { type, message }
 */
export function getPackageWarnings(pkg) {
    const warnings = [];
    const daysLeft = pkg.daysLeft ?? 0;

    if (daysLeft > 0 && daysLeft <= 3) {
        warnings.push({ type: 'low_days', message: `Solo quedan ${daysLeft} día${daysLeft !== 1 ? 's' : ''} en este paquete.` });
    }

    // Only warn on expiry if package was activated under the new system (firstUsedAt set)
    // Packages with expiresAt but no firstUsedAt used the old short-expiry logic and should not trigger
    if (pkg.firstUsedAt && pkg.expiresAt && daysLeft > 0 && pkg.packageType !== '1-day') {
        const daysUntilExpiry = Math.ceil((new Date(pkg.expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiry <= 5 && daysUntilExpiry > 0) {
            warnings.push({ type: 'expiring_soon', message: `Vence en ${daysUntilExpiry} día${daysUntilExpiry !== 1 ? 's' : ''}.` });
        }
    }

    return warnings;
}

